/**
 * Phone Badge Component
 * Displays phone number with validation status
 */

import type { PhoneInfo } from '../api/subscribers.api';

interface PhoneBadgeProps {
  phone: PhoneInfo;
  showRaw?: boolean;
  className?: string;
}

const validConfig = {
  bgColor: 'bg-green-50',
  textColor: 'text-green-700',
  borderColor: 'border-green-200',
  icon: '✓',
  label: 'Valid',
};

const invalidConfig = {
  bgColor: 'bg-red-50',
  textColor: 'text-red-700',
  borderColor: 'border-red-200',
  icon: '⚠',
  label: 'Invalid',
};

const missingConfig = {
  bgColor: 'bg-gray-50',
  textColor: 'text-gray-500',
  borderColor: 'border-gray-200',
  icon: '−',
  label: 'Missing',
};

export function PhoneBadge({ phone, showRaw = false, className = '' }: PhoneBadgeProps) {
  // Determine state
  let config = missingConfig;
  let displayValue = 'No phone';

  if (phone.normalized) {
    if (phone.valid) {
      config = validConfig;
      displayValue = phone.formatted;
    } else {
      config = invalidConfig;
      displayValue = phone.formatted || phone.raw || 'Invalid';
    }
  }

  return (
    <div className={`inline-flex flex-col ${className}`}>
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
      >
        <span className="text-[10px]">{config.icon}</span>
        <span>{displayValue}</span>
      </span>
      {showRaw && phone.raw && phone.raw !== phone.normalized && (
        <span className="text-xs text-gray-400 mt-0.5 ml-1">
          Raw: {phone.raw}
        </span>
      )}
    </div>
  );
}

/**
 * Simple phone display for tables
 */
export function PhoneDisplay({ phone }: { phone: PhoneInfo }) {
  if (!phone.normalized) {
    return <span className="text-gray-400 text-sm">−</span>;
  }

  const colorClass = phone.valid ? 'text-gray-700' : 'text-red-600';
  const icon = phone.valid ? null : <span className="text-red-500 mr-1">⚠</span>;

  return (
    <span className={`text-sm ${colorClass} flex items-center`}>
      {icon}
      {phone.formatted}
    </span>
  );
}

/**
 * Phone with copy button
 */
export function PhoneWithCopy({ phone }: { phone: PhoneInfo }) {
  if (!phone.normalized) {
    return <span className="text-gray-400">−</span>;
  }

  const handleCopy = () => {
    if (phone.normalized) {
      navigator.clipboard.writeText(phone.normalized);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className={`text-sm ${phone.valid ? 'text-gray-700' : 'text-red-600'}`}>
        {phone.formatted}
      </span>
      <button
        onClick={handleCopy}
        className="text-gray-400 hover:text-gray-600 transition-colors"
        title="Copy to clipboard"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      </button>
    </div>
  );
}
